import React from 'react';
import './CaseStudies.css';

interface Capability {
  title: string;
  category: string;
  description: string;
  technologies: string[];
  benefits: string[];
}

const capabilitiesData: Capability[] = [
  {
    title: 'AI Agent Development',
    category: 'Core Capability',
    description: 'We specialize in building intelligent autonomous agents that can understand context, make decisions, and take actions. Our agents leverage the latest LLM technologies and can be customized for any business domain.',
    technologies: [
      'GPT-4, Claude, and custom LLMs',
      'LangChain and agent frameworks',
      'RAG (Retrieval-Augmented Generation)',
      'Multi-agent orchestration systems'
    ],
    benefits: [
      'Automate complex workflows',
      'Reduce operational costs by 40-60%',
      'Scale operations without headcount',
      'Provide 24/7 intelligent responses'
    ]
  },
  {
    title: 'Strategic AI Consulting',
    category: 'Advisory Services',
    description: 'Not sure where to start with AI? We help you identify high-impact opportunities, assess technical feasibility, and create actionable roadmaps that align AI initiatives with your business goals.',
    technologies: [
      'AI readiness assessments',
      'Technology stack evaluation',
      'ROI modeling and forecasting',
      'Implementation planning'
    ],
    benefits: [
      'Clear AI strategy and priorities',
      'Avoid costly missteps',
      'Realistic timeline and budget',
      'Buy-in from stakeholders'
    ]
  },
  {
    title: 'Rapid Prototyping',
    category: 'Proof of Concept',
    description: 'Test your AI ideas quickly with our rapid prototyping service. We build working prototypes in weeks, not months, allowing you to validate concepts and secure internal buy-in before full investment.',
    technologies: [
      'Fast iteration cycles (2-4 weeks)',
      'Cloud-native deployment',
      'Interactive demos and dashboards',
      'User feedback integration'
    ],
    benefits: [
      'Validate ideas before major investment',
      'Demonstrate value to stakeholders',
      'Learn what works for your use case',
      'Fast path from concept to production'
    ]
  }
];

const CaseStudies: React.FC = () => {
  return (
    <section className="case-studies" id="case-studies">
      <div className="case-studies-container">
        <div className="section-header">
          <h2 className="section-title">Our Capabilities</h2>
          <p className="section-subtitle">
            What we bring to your AI transformation journey
          </p>
        </div>
        <div className="case-studies-grid">
          {capabilitiesData.map((capability, index) => (
            <div key={index} className="case-study-card">
              <div className="case-study-header">
                <span className="case-study-industry">{capability.category}</span>
                <h3 className="case-study-title">{capability.title}</h3>
              </div>
              <div className="case-study-content">
                <div className="case-study-section">
                  <h4>What We Do</h4>
                  <p>{capability.description}</p>
                </div>
                <div className="case-study-section">
                  <h4>Technologies & Approach</h4>
                  <ul className="case-study-results">
                    {capability.technologies.map((tech, idx) => (
                      <li key={idx}>
                        <span className="result-icon">🔧</span>
                        {tech}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="case-study-section">
                  <h4>Benefits for You</h4>
                  <ul className="case-study-results">
                    {capability.benefits.map((benefit, idx) => (
                      <li key={idx}>
                        <span className="result-icon">✓</span>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="startup-message">
          <div className="startup-message-content">
            <h3>🌱 New Company, Deep Expertise</h3>
            <p>
              While Moosera is a new venture, our team members bring years of experience from leading 
              tech companies and AI research labs. We're eager to prove ourselves and build long-term 
              partnerships. Your success is our success, and we're committed to delivering exceptional 
              value from day one.
            </p>
            <div className="startup-cta">
              <p><strong>Ready to be among our first clients?</strong> We're offering competitive rates 
              and personalized attention as we build our portfolio. Let's grow together.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
